import { useQuery } from '@apollo/client';
import { Close } from '@mui/icons-material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Dialog, IconButton, Skeleton } from '@mui/material';
import { useParams } from '@tanstack/react-router';
import type React from 'react';
import { type KeyboardEvent, useCallback, useContext, useRef, useState } from 'react';
import { PROXY_FORMAT_SLUG } from 'src/constants';
import { useMaybeLibrarySearchResults } from 'src/context/LibrarySearchResultsContext';
import { buildAllFieldsQ, buildAllMFQ } from 'src/dynamoQueries/queryHelpers';
import { buildSimpleOSQuery } from 'src/dynamoQueries/search';
import { getCurrentOsResultById, getCurrIdxByItemId, getOSResultTypeNameById } from 'src/helpers';
import { cleanPayload, getMetadataFieldValue } from 'src/helpers/common';
import { processSearchQuery } from 'src/helpers/searchHelper';
import { SharedLinkContext } from 'src/SharedLinkPage/context';
import MediaPreviewRouter from './MediaPreviewRouter';

import { useStyles } from './styles';

interface MediaCardPreviewModalProps {
  itemID?: string;
  isShared?: boolean;
  pagination?: boolean;
  onClose: () => void;
}

interface ContentObject {
  id: string;
  items?: Array<{ objectId: string }>;
}

interface OpenSearchResult {
  contentObject: ContentObject;
}

interface OpenSearchData {
  opensearch?: {
    results: OpenSearchResult[];
  };
}

interface FormatData {
  id?: string;
  online?: boolean;
}

interface LinkInfo {
  collectionId?: string;
}

interface SharedLinkContextType {
  linkInfo: LinkInfo | null;
}

const MediaCardPreviewModal: React.FC<MediaCardPreviewModalProps> = ({
  itemID = '',
  isShared = false,
  pagination = true,
  onClose,
}) => {
  const params = useParams({ strict: false });
  const galleryId = (params as { galleryId?: string }).galleryId;
  const classes = useStyles();
  const [currIdx, setCurrIdx] = useState<number | null>(null);
  const { linkInfo } = useContext(SharedLinkContext) as SharedLinkContextType;
  const searchResultsContext = useMaybeLibrarySearchResults();
  const contextItems = !isShared ? searchResultsContext?.items || [] : [];
  const useContextItems = contextItems.length > 0;

  const { data: opensearchResults } = useQuery<OpenSearchData>(
    // assetsByCollectionId,
    buildSimpleOSQuery(),
    {
      // cache only now, we dont want to make this call again, we just want its data
      fetchPolicy: 'cache-first',
      skip: useContextItems,
      variables: {
        q: processSearchQuery(window.location.search, isShared ? linkInfo?.collectionId : galleryId).replace(
          '%3A',
          ':',
        ),
      },
    },
  );

  const itemIDs: string[] = useContextItems
    ? contextItems.map((item) => item.id)
    : opensearchResults?.opensearch?.results.map((res) => res?.contentObject.id) || [];
  const getCurrItemID = (index: number | null, ids: string[]): string => {
    if (index == null || index < 0) {
      return itemID;
    }
    return ids[index];
  };
  if (currIdx === null && itemIDs.length > 0 && itemID) {
    setCurrIdx(getCurrIdxByItemId(itemID, itemIDs));
  }
  const id = getCurrItemID(currIdx, itemIDs);

  const resolveItem = (formatId: string): { contentObject?: ContentObject; typename: string } => {
    if (useContextItems) {
      const item = contextItems.find((it) => it.id === formatId);
      return { contentObject: item as ContentObject | undefined, typename: item?.__typename ?? '' };
    }
    const result = getCurrentOsResultById(formatId, opensearchResults);
    return {
      contentObject: result?.contentObject,
      typename: getOSResultTypeNameById(formatId, opensearchResults),
    };
  };

  const getFormatVariables = (formatId: string): Record<string, string> => {
    const { contentObject, typename } = resolveItem(formatId);
    if (typename.includes('Collection')) {
      const firstItem = contentObject?.items?.[0];
      return { asset_Items_ObjectId: firstItem?.objectId || '', fmtType_Slug: PROXY_FORMAT_SLUG };
    }
    return { asset_Id: formatId, fmtType_Slug: PROXY_FORMAT_SLUG };
  };
  // Get next and previous IDs
  const nextId = currIdx !== null && currIdx < itemIDs?.length - 1 ? itemIDs[currIdx + 1] : null;
  const prevId = currIdx !== null && currIdx > 0 ? itemIDs[currIdx - 1] : null;

  // fetch proxy data, skip if we have no id
  const { data: format, loading: loadingProxyData } = useQuery(buildAllFieldsQ(), {
    skip: !id,
    variables: getFormatVariables(id),
  });
  const { data: nextFormat } = useQuery(buildAllFieldsQ(), {
    skip: !nextId,
    variables: nextId ? getFormatVariables(nextId) : undefined,
  });

  const { data: prevFormat } = useQuery(buildAllFieldsQ(), {
    skip: !prevId,
    variables: prevId ? getFormatVariables(prevId) : undefined,
  });

  const cleanedFormat: FormatData = cleanPayload<FormatData>(format)[0] || {};
  const cleanedNextFormat: FormatData | undefined = cleanPayload<FormatData>(nextFormat)[0];
  const cleanedPrevFormat: FormatData | undefined = cleanPayload<FormatData>(prevFormat)[0];

  const { data: formatData, loading: loadingMimeType } = useQuery(buildAllMFQ(), {
    skip: !cleanedFormat?.id,
    variables: { globalId: cleanedFormat?.id, definition_Name_In: 'mimetype' },
  });

  const onClickClose = () => {
    onClose();
  };

  const isLoading = loadingMimeType || loadingProxyData;

  const mimetype = getMetadataFieldValue(formatData, 'mimetype') as string | undefined;

  // Cross-fade transition state: when navigating, fade out old content before showing new
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Stale-while-revalidate: keep showing the previous preview while the next one loads.
  // This prevents the grey skeleton flash when clicking through items.
  const lastLoadedRef = useRef<{
    id: string;
    mimetype: string | undefined;
    format: FormatData;
    nextFormat: FormatData | undefined;
    prevFormat: FormatData | undefined;
    itemType: string;
  } | null>(null);

  // Update the ref whenever fresh data arrives — including items with no format (media unavailable).
  // Without this, items missing a proxy format would leave stale data from the previous item.
  if (!isLoading) {
    lastLoadedRef.current = {
      id,
      mimetype,
      format: cleanedFormat,
      nextFormat: cleanedNextFormat,
      prevFormat: cleanedPrevFormat,
      itemType: resolveItem(id).typename,
    };
    // End transition when fresh data arrives
    if (isTransitioning) {
      setIsTransitioning(false);
    }
  }

  // Show stale data during loading (previous item) to avoid flash.
  const displayData = lastLoadedRef.current;
  const isInitialLoad = isLoading && !lastLoadedRef.current;

  const handleTransitionEnd = useCallback(() => {
    setIsTransitioning(false);
  }, []);

  const goNext = () => {
    if (currIdx !== null) {
      setIsTransitioning(true);
      setCurrIdx(currIdx + 1);
    }
  };
  const goPrev = () => {
    if (currIdx !== null) {
      setIsTransitioning(true);
      setCurrIdx(currIdx - 1);
    }
  };
  const keyDownHandler = (k: KeyboardEvent<HTMLDivElement>) => {
    if (k.keyCode === 37 && !getDisabledState(true)) {
      goPrev();
    }
    if (k.keyCode === 39 && !getDisabledState(false)) {
      goNext();
    }
  };

  const getDisabledState = (prev = false): boolean => {
    if (prev && currIdx === 0) {
      return true;
    }
    if (!prev && currIdx === itemIDs?.length - 1) {
      return true;
    }
    return false;
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          overflow: 'hidden',
          boxShadow: 'none',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.92)' : 'rgba(0, 0, 0, 0.85)'),
          },
        },
      }}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: keyboard navigation wrapper for modal, interactive elements inside have proper accessibility */}
      <div onKeyDown={keyDownHandler} style={{ width: '100%', height: '100%' }}>
        <IconButton
          className={classes.closeIcon}
          onClick={onClickClose}
          size="large"
          sx={{ color: (theme) => theme.palette.common.white }}
        >
          <Close />
        </IconButton>
        {pagination && (
          <>
            <IconButton
              className={classes.nextIcon}
              onClick={goNext}
              disabled={getDisabledState(false)}
              size="large"
              sx={{ color: (theme) => theme.palette.common.white }}
            >
              <NavigateNextIcon />
            </IconButton>
            <IconButton
              className={classes.prevIcon}
              onClick={goPrev}
              disabled={getDisabledState(true)}
              size="large"
              sx={{ color: (theme) => theme.palette.common.white }}
            >
              <NavigateBeforeIcon />
            </IconButton>
          </>
        )}
        {isInitialLoad ? (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '60%',
              height: '60%',
              borderRadius: 2,
              bgcolor: (theme) => theme.palette.action.disabledBackground,
            }}
          />
        ) : displayData ? (
          <MediaPreviewRouter
            id={displayData.id}
            mimetype={displayData.mimetype}
            format={displayData.format}
            nextFormat={displayData.nextFormat}
            prevFormat={displayData.prevFormat}
            itemType={displayData.itemType}
            isShared={isShared}
            isTransitioning={isTransitioning}
            onTransitionEnd={handleTransitionEnd}
          />
        ) : null}
      </div>
    </Dialog>
  );
};

export default MediaCardPreviewModal;
